/**
 * Extracts trailing digits from a string and returns them as a number.
 * Returns 0 if no trailing digits found.
 */
export function extractTrailingNumber(str: string): number {
  let i = str.length - 1;
  while (i >= 0 && str[i] >= "0" && str[i] <= "9") {
    i--;
  }
  const digits = str.slice(i + 1);
  return digits.length > 0 ? Number(digits) : 0;
}

/**
 * Finds the first available number starting from 1 that is not in the given Set.
 */
export function findNextDisplayNumber(usedNumbers: Set<number>): number {
  let n = 1;
  while (usedNumbers.has(n)) {
    n++;
  }
  return n;
}

/**
 * Strips `results` from each search entry before saving to sessionStorage.
 */
export function stripSearchResultsForSave(searches: Record<number, object>): Record<number, unknown> {
  const stripped: Record<number, unknown> = {};
  for (const key of Object.keys(searches)) {
    const tabId = Number(key);
    stripped[tabId] = { ...searches[tabId], results: [] };
  }
  return stripped;
}

/**
 * Скалярные/простые поля состояния вкладки, которые восстанавливаются из
 * localStorage «как есть» при перезагрузке страницы. Сложные поля (details,
 * results, brandsAliases, searchStatuses, ...) восстанавливаются отдельно.
 *
 * ВАЖНО: `reorderId` обязан быть здесь — иначе после F5 id исходного отказанного
 * заказа теряется и add-to-cart перепроценки не свяжет старый и новый заказ.
 */
export const RESTORED_SEARCH_SIMPLE_KEYS = [
  "searchValue",
  "requestId",
  "time_from",
  // отметка «поиск остановлен» (CAB-586): должна пережить перезагрузку, иначе
  // после F5 остановленная проценка снова переполлится по живому requestId
  "stopped",
  "loading",
  "quickFilterValue",
  "selectedBrand",
  "selectedBrid",
  "vehicleMetadata",
  "showVehicleMetadata",
  "cartQuantity",
  "isImagePanelVisible",
  "isNewSearch",
  "gridState",
  "scheme",
  "reorderId",
] as const;

/**
 * Возвращает подмножество сохранённого состояния вкладки только по
 * whitelist-ключам {@link RESTORED_SEARCH_SIMPLE_KEYS} (undefined-поля пропускаются).
 */
export function pickRestoredSimpleKeys(src: Record<string, unknown>): Record<string, unknown> {
  const picked: Record<string, unknown> = {};
  for (const key of RESTORED_SEARCH_SIMPLE_KEYS) {
    if (typeof src[key] !== "undefined") {
      picked[key] = src[key];
    }
  }
  return picked;
}

/**
 * Filters saved plugin IDs against the list of currently available plugin IDs.
 * If savedIds is empty, returns all availableIds.
 * If filtering results in an empty list, returns all availableIds as fallback.
 */
export function filterValidPluginIds(savedIds: number[], availableIds: number[]): number[] {
  // id площадок из userPlugins могут прийти строкой (с бэка), а сохранённый выбор пишется
  // числом — строгий includes промахивался, и ранее выбранные поставщики сбрасывались на
  // «все» в КАЖДОЙ новой вкладке. Сравниваем и возвращаем всегда как number (CAB-586).
  const available = availableIds.map(Number);
  if (savedIds.length === 0) {
    return available;
  }
  const availableSet = new Set(available);
  const filtered = savedIds.map(Number).filter((id) => availableSet.has(id));
  return filtered.length > 0 ? filtered : available;
}
